import { IColumnConfig } from "../../../components/ui/baseDataTable";
import { SanctionEventDetail } from "../../../api/sanctionEvents";
import { CreditAccount } from "../../../api/credit_accounts";
import { OptionNameValuePair } from "../../../lib/interfaces/types";
import {
    CreditFacilityLookup,
    enrichSanctionEventDetails,
    SANCTION_EVENT_TYPE_OPTIONS,
} from "../Logic/sanctionEventMappings";
import {
    CREDIT_FACILITY_SHORT_LABELS,
    FacilityCategory,
} from "../../creditAccounts/logic/creditAccountMappings";
import {
    SanctionEventRow,
    SanctionEventsTableVM,
} from "../Logic/useSanctionEventsTableVM";
import { SanctionEventDetailsCell } from "./SanctionEventDetailsCell";

const mergeCategoryDetails = (
    details: SanctionEventDetail[],
    category: FacilityCategory,
    updatedCategoryDetails: SanctionEventDetail[],
    getCreditFacilityForLoanAccount: CreditFacilityLookup,
): SanctionEventDetail[] =>
    enrichSanctionEventDetails(details, getCreditFacilityForLoanAccount)
      .filter((detail) => detail.facility_category !== category)
      .map((detail) => detail.raw)
      .concat(updatedCategoryDetails);

export const toLoanAccountOption = (
    account: CreditAccount,
): OptionNameValuePair<string> => ({
    value: account.account_number,
    name: `${account.ext_account_number} - ${CREDIT_FACILITY_SHORT_LABELS[account.credit_facility]}`,
});

const makeFacilityColumn = (
    category: FacilityCategory,
    vm: Pick<
      SanctionEventsTableVM,
      | "updateDetails"
      | "isSaving"
      | "getCreditFacilityForLoanAccount"
      | "getLoanAccountOptions"
    >,
): IColumnConfig<SanctionEventRow, SanctionEventDetail[]> => ({
    headerName:
      category === FacilityCategory.TERM_LOAN
        ? "Term Loan"
        : category === FacilityCategory.WORKING_CAPITAL
        ? "Working Capital"
        : "Non Fund Based",
    field: "details",
    type: "custom",
    getValue: (row) => row.details,
    isEditable: !vm.isSaving,
    tableCellStyle: { minWidth: "220px", maxWidth: "260px" },
    headerStyle: { minWidth: "220px", maxWidth: "260px" },
    render: (rowIndex, value) => (
      <SanctionEventDetailsCell
        category={category}
        details={value ?? []}
        disabled={vm.isSaving}
        getCreditFacilityForLoanAccount={vm.getCreditFacilityForLoanAccount}
        loanAccountOptions={vm.getLoanAccountOptions(category)}
        onApply={(updatedDetails) =>
          vm.updateDetails(
            rowIndex,
            mergeCategoryDetails(
              value ?? [],
              category,
              updatedDetails,
              vm.getCreditFacilityForLoanAccount,
            ),
          )
        }
      />
    ),
});

export const makeSanctionEventColumns = (
    vm: Pick<
      SanctionEventsTableVM,
      | "updateRow"
      | "updateDetails"
      | "isSaving"
      | "getCreditFacilityForLoanAccount"
      | "getLoanAccountOptions"
    >,
): IColumnConfig<SanctionEventRow>[] => [
    {
      headerName: "Sanction Authority Name",
      field: "authority_name",
      type: "text",
      getValue: (row) => row.authority_name ?? "",
      isEditable: !vm.isSaving,
      onChange: vm.updateRow,
      textCellProps: { truncateChars: 36, numberOfRows: 1 },
      tableCellStyle: { minWidth: "210px", maxWidth: "240px" },
      headerStyle: { minWidth: "210px", maxWidth: "240px" },
    },
    {
      headerName: "Event Type",
      field: "event_type",
      type: "select",
      getValue: (row) => row.event_type,
      isEditable: !vm.isSaving,
      options: SANCTION_EVENT_TYPE_OPTIONS,
      onChange: vm.updateRow,
      selectCellProps: {
        placeholder: "event type",
        className: "px-0",
        labelClassName: "px-0 py-1 text-xs min-w-[160px]",
        dropdownClassName: "min-w-[240px]",
      },
      tableCellStyle: { minWidth: "190px", maxWidth: "230px" },
      headerStyle: { minWidth: "190px", maxWidth: "230px" },
    },
    {
      headerName: "Date of Sanction",
      field: "sanction_date",
      type: "date",
      getValue: (row) => row.sanction_date ?? "",
      isEditable: !vm.isSaving,
      onChange: vm.updateRow,
      tableCellStyle: { minWidth: "160px", maxWidth: "180px" },
      headerStyle: { minWidth: "160px", maxWidth: "180px" },
    },
    makeFacilityColumn(FacilityCategory.TERM_LOAN, vm),
    makeFacilityColumn(FacilityCategory.WORKING_CAPITAL, vm),
    makeFacilityColumn(FacilityCategory.NON_FUND_BASED, vm),
];
